import { icon } from "../lib/icons";

const TOOLS = ["Spreadsheet", "Google Calendar", "Flight Circle", "FlightLogger", "Paper binder", "Custom system"];
const URGENCIES = ["Just exploring", "Within 3 months", "This month", "Urgent"];

export function feedbackHTML(): string {
  return `
<template x-if="$store.ui.feedbackOpen">
  <div class="modal-backdrop" @click="$store.ui.closeFeedback()" @keydown.escape.window="$store.ui.closeFeedback()" x-data="feedback()">
    <div class="modal" style="max-width: 560px" role="dialog" aria-modal="true" aria-labelledby="feedback-modal-title" @click.stop
      x-init="window.autoFocus($el)"
      @keydown.tab="window.trapTab($event, $el, false)"
      @keydown.shift.tab="window.trapTab($event, $el, true)">
      <div class="modal-h">
        <h2 id="feedback-modal-title" x-text="step < 3 ? 'Quick feedback' : 'Thanks for the look'"></h2>
        <span class="muted mono" style="margin-left: 10px; font-size: 11px"
          x-text="step < 3 ? \`step \${step} of 2\` : ''"></span>
        <button type="button" class="btn btn-icon btn-ghost" style="margin-left: auto"
          aria-label="Close feedback" @click="$store.ui.closeFeedback()">${icon("x", 14)}</button>
      </div>

      <template x-if="step === 1">
        <div>
          <div class="modal-body">
            <p class="muted" style="margin-top: 0">Which part of the demo was most useful?</p>
            <div class="fb-areas">
              ${areaButton("schedule", "Schedule visibility", icon("calendar", 14))}
              ${areaButton("warnings", "Conflict warnings", icon("warn", 14))}
              ${areaButton("dispatch", "Dispatch board", icon("tower", 14))}
              ${areaButton("progress", "Student progress", icon("grad", 14))}
              ${areaButton("pricing", "Simpler pricing", icon("star", 14))}
            </div>
            <label class="field">
              <span class="field-label">Anything else worth saying?</span>
              <textarea class="textarea" rows="2" x-model="form.note"
                placeholder="What's missing? What would make you switch?"></textarea>
            </label>
          </div>
          <div class="modal-foot">
            <button class="btn" @click="$store.ui.closeFeedback()">Cancel</button>
            <button class="btn btn-primary" :disabled="!form.mostUseful" @click="step = 2">
              Continue ${icon("arrowR", 12)}
            </button>
          </div>
        </div>
      </template>

      <template x-if="step === 2">
        <div>
          <div class="modal-body">
            <p class="muted" style="margin-top: 0">Tell us about your operation so we can prioritize follow-up.</p>
            <div class="grid-2">
              <label class="field">
                <span class="field-label">Aircraft in fleet</span>
                <select class="select" x-model="form.aircraftCount">
                  <option value="">Select</option>
                  <option>1–3</option><option>4–8</option><option>9–15</option><option>16+</option>
                </select>
              </label>
              <label class="field">
                <span class="field-label">Active students</span>
                <select class="select" x-model="form.activeStudents">
                  <option value="">Select</option>
                  <option>0–10</option><option>11–25</option><option>26–60</option><option>60+</option>
                </select>
              </label>
            </div>
            <div class="grid-2">
              <label class="field">
                <span class="field-label">Instructors on staff</span>
                <select class="select" x-model="form.instructors">
                  <option value="">Select</option>
                  <option>1–2</option><option>3–5</option><option>6–10</option><option>10+</option>
                </select>
              </label>
              <label class="field">
                <span class="field-label">Current tool</span>
                <select class="select" x-model="form.currentTool">
                  <option value="">Select</option>
                  ${TOOLS.map(t => `<option>${t}</option>`).join("")}
                </select>
              </label>
            </div>
            <div class="field" role="radiogroup" aria-label="Replacement urgency">
              <span class="field-label" id="urgency-label">How soon would you replace your current tool?</span>
              <div style="display: flex; gap: 6px; flex-wrap: wrap" aria-labelledby="urgency-label">
                ${URGENCIES.map(u => `
                  <button type="button" class="filter-chip"
                    role="radio"
                    :aria-checked="form.urgency === '${u}' ? 'true' : 'false'"
                    :class="form.urgency === '${u}' ? 'on' : ''"
                    @click="form.urgency = '${u}'">${u}</button>
                `).join("")}
              </div>
            </div>
          </div>
          <div class="modal-foot">
            <button class="btn" @click="step = 1">Back</button>
            <button class="btn btn-primary"
              :disabled="!form.urgency || !form.aircraftCount || busy"
              @click="submit()">
              <span x-show="!busy">Submit feedback ${icon("send", 12)}</span>
              <span x-show="busy">Sending…</span>
            </button>
          </div>
        </div>
      </template>

      <template x-if="step === 3">
        <div style="padding: 28px; text-align: center">
          <div class="success-ring">${icon("check", 28, 2.4, "white")}</div>
          <h3 style="margin: 16px 0 6px; font-size: 18px">Got it. Thanks.</h3>
          <p class="muted" style="max-width: 380px; margin: 0 auto 16px"
            x-html="successMessage()"></p>
          <div style="display: flex; gap: 8px; justify-content: center">
            <button class="btn btn-primary" @click="$store.ui.closeFeedback()">Back to the demo</button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<template x-if="$store.ui.feedbackPromptVisible && !$store.ui.feedbackOpen">
  <div class="fb-prompt" role="region" aria-label="Feedback prompt">
    ${icon("sparkle", 18, 1.6, "#7aa9f5")}
    <p>You've explored a couple of views. Mind sharing 30 seconds of feedback?</p>
    <button type="button" class="btn btn-sm btn-primary" @click="$store.ui.openFeedback()">Sure</button>
    <button type="button" class="btn-icon btn btn-ghost" style="color: white; padding: 4px"
      aria-label="Dismiss feedback prompt"
      @click="$store.ui.dismissFeedbackPrompt()">${icon("x", 13)}</button>
  </div>
</template>
`;
}

function areaButton(id: string, label: string, ico: string): string {
  return `
    <button class="fb-area" :class="form.mostUseful === '${id}' ? 'on' : ''"
      @click="form.mostUseful = '${id}'">
      <span style="display: flex; align-items: center; gap: 8px">${ico}<span>${label}</span></span>
    </button>
  `;
}
